import React from "react";
import FoundationTemplate from "@/components/foundations/FoundationTemplate";

/**
 * The six Scientific Foundations pillar pages, kept in a single file
 * for authoring speed. Each named export is a full page component; App.js
 * imports them individually.
 */

export const ExercisePhysiology = () => (
  <FoundationTemplate
    pageKey="exercise-physiology"
    breadcrumbLabel="Exercise Physiology"
    overline="Pillar 01 · Exercise Physiology"
    headline="Movement is the most direct input"
    headlineAccent="we have into the nervous system."
    lead="Exercise physiology studies how prescribed movement changes the human system — cardiovascular, autonomic, metabolic, and neurological. It is the discipline through which Adaptive Physiology™ delivers its primary stimulus."
    whyItMatters={{
      title: "Dose. Modality. Timing. All of it matters.",
      paragraphs: [
        "Aerobic base work, resistance loading, and respiratory pacing produce distinct, well-characterized physiological responses. The choice of stimulus, and how it is dosed, determines what the system adapts to.",
        "Adaptive Physiology™ treats exercise not as fitness accessory but as a signaling technology — the most reliable way to change autonomic tone, HRV headroom, BDNF availability, and the physiological substrate behavior runs on.",
      ],
    }}
    concepts={[
      { title: "Zone-based aerobic training", body: "Different intensities produce different adaptations. Zone 2 uniquely trains mitochondrial density and parasympathetic access." },
      { title: "Progressive overload & recovery", body: "Adaptation happens between doses of load, not during them — recovery is where the loop closes." },
      { title: "Respiratory & vagal loading", body: "Structured breathing patterns act on the vagus nerve directly, expanding autonomic capacity." },
      { title: "HRV-guided prescription", body: "Autonomic readiness modulates what stimulus the system can actually convert into adaptation on a given day." },
    ]}
    representative={[
      "Foundational aerobic base research (San Millán, Seiler)",
      "Resistance training & neural drive (Schoenfeld, Häkkinen)",
      "Respiratory-vagal coupling (Gerritsen & Band, Zaccaro et al.)",
      "HRV-guided training (Kiviniemi, Vesterinen)",
    ]}
    whereItFits={{
      principles: [
        "The nervous system adapts to what it receives.",
        "Repeated inputs create adaptations.",
        "Intentional inputs create new adaptations.",
      ],
      loopStage: "Matched Adaptive Intervention · movement becomes the physiological input.",
      frameworkStep: "02 · Prescribe the Stimulus.",
      applications: [
        { label: "Performance", to: "/applications/performance" },
        { label: "Stress & Burnout", to: "/applications/stress-burnout" },
        { label: "Recovery", to: "/applications/recovery" },
      ],
    }}
    nextPillar={{ label: "Neuroplasticity", to: "/foundations/neuroplasticity" }}
  />
);

export const Neuroplasticity = () => (
  <FoundationTemplate
    pageKey="neuroplasticity"
    breadcrumbLabel="Neuroplasticity"
    overline="Pillar 02 · Neuroplasticity"
    headline="The nervous system was designed"
    headlineAccent="to be trainable."
    lead="Neuroplasticity — the capacity of the nervous system to reorganize its structure and function in response to experience — is the mechanism that makes Adaptive Physiology™ possible. Without it, retraining would be a metaphor."
    whyItMatters={{
      title: "Repeated experience changes the substrate.",
      paragraphs: [
        "Structural, chemical, and functional changes accumulate in response to sustained input. What fires together, wires together — and what wires together becomes the physiological default.",
        "Adaptive Physiology™ is a deliberate application of this fact: give the nervous system consistent, well-dosed signals, and it rewires the responses it defaults to.",
      ],
    }}
    concepts={[
      { title: "Hebbian & functional plasticity", body: "The rule that co-active neural populations strengthen their connections — the biology of learned defaults." },
      { title: "BDNF and neurotrophic signaling", body: "Brain-derived neurotrophic factor supports the encoding of new patterns; exercise is one of its most reliable inputs." },
      { title: "Autonomic learning", body: "The autonomic nervous system itself is trainable — HRV widens, vagal tone strengthens, thresholds shift." },
      { title: "Repetition & consolidation", body: "New patterns require repeated exposure over sufficient time to become the new default." },
    ]}
    representative={[
      "Cellular mechanisms of learning & memory (Kandel)",
      "Exercise, BDNF, and cognitive plasticity (Ratey, Cotman)",
      "Autonomic learning & neurovisceral integration (Thayer & Lane)",
      "Experience-dependent plasticity across the lifespan (Merzenich)",
    ]}
    whereItFits={{
      principles: [
        "The nervous system adapts.",
        "Intentional inputs create new adaptations.",
      ],
      loopStage: "Recovery → Homeostasis · completed cycles give the nervous system a new response to learn.",
      frameworkStep: "04 · Retrain the Default.",
      applications: [
        { label: "Habit & Behavior Change", to: "/applications/habit-change" },
        { label: "Anxiety & Regulation", to: "/applications/anxiety-regulation" },
        { label: "Trauma Adaptation", to: "/applications/trauma-adaptation" },
      ],
    }}
    nextPillar={{ label: "Stress Physiology", to: "/foundations/stress-physiology" }}
  />
);

export const StressPhysiology = () => (
  <FoundationTemplate
    pageKey="stress-physiology"
    breadcrumbLabel="Stress Physiology"
    overline="Pillar 03 · Stress Physiology"
    headline="Stress is not the problem."
    headlineAccent="Chronic dysregulation is."
    lead="Stress physiology studies how the body mobilizes, resolves, and recovers from load. Adaptive Physiology™ uses these mechanisms — HPA axis, autonomic balance, allostatic regulation — as measurable and trainable targets."
    whyItMatters={{
      title: "The stress response is meant to open and close.",
      paragraphs: [
        "A regulated stress response engages under demand and releases when the demand ends. A dysregulated one stays engaged, narrows autonomic capacity, and produces the physiology from which many recurring behaviors are generated.",
        "Adaptive Physiology™ measures these signals, and prescribes inputs designed to widen — not blunt — the stress response.",
      ],
    }}
    concepts={[
      { title: "Allostasis & allostatic load", body: "The cumulative cost of chronic activation — a measurable, modifiable systemic burden." },
      { title: "HPA axis & cortisol rhythm", body: "The shape of the diurnal cortisol curve — flattened under load, restorable through structured input." },
      { title: "Autonomic balance & HRV", body: "The functional ratio between sympathetic activation and parasympathetic recovery." },
      { title: "Polyvagal framework", body: "A model of how the vagal system organizes safety, connection, and threat states." },
    ]}
    representative={[
      "Allostasis and allostatic load (McEwen)",
      "Chronic stress & endocrine adaptation (Sapolsky)",
      "Neurovisceral integration model of HRV (Thayer & Lane)",
      "Polyvagal theory & autonomic regulation (Porges)",
    ]}
    whereItFits={{
      principles: [
        "Physiological state influences behavior.",
        "Repeated inputs create adaptations.",
      ],
      loopStage: "Sympathetic Activation → Parasympathetic Recovery · load is followed by restoration.",
      frameworkStep: "01 · Assess the State.",
      applications: [
        { label: "Stress & Burnout", to: "/applications/stress-burnout" },
        { label: "Anxiety & Regulation", to: "/applications/anxiety-regulation" },
        { label: "Trauma Adaptation", to: "/applications/trauma-adaptation" },
      ],
    }}
    nextPillar={{ label: "Behavioral Science", to: "/foundations/behavioral-science" }}
  />
);

export const BehavioralScience = () => (
  <FoundationTemplate
    pageKey="behavioral-science"
    breadcrumbLabel="Behavioral Science"
    overline="Pillar 04 · Behavioral Science"
    headline="Behavior is the output."
    headlineAccent="The environment writes the input."
    lead="Behavioral science studies how environment, reinforcement, and social context shape human action. Adaptive Physiology™ uses these tools not to override the nervous system, but to give it consistent inputs it can adapt to."
    whyItMatters={{
      title: "Structure delivers signal. Signal delivers adaptation.",
      paragraphs: [
        "Environmental design, cueing, reinforcement schedules, and social accountability aren’t motivational tricks — they are the mechanisms by which a physiological signal is delivered consistently enough to matter.",
        "Adaptive Physiology™ treats behavioral science as the delivery system of the loop’s Reinforce stage.",
      ],
    }}
    concepts={[
      { title: "Environmental design", body: "Behavior is shaped by the environment as much as by the person; changing the environment changes the input." },
      { title: "Reinforcement & schedules", body: "How consequences and timing determine whether a behavior becomes an established pattern." },
      { title: "Habit formation", body: "The behavioral output of a physiological state, and the substrate through which new states become stable." },
      { title: "Social & contextual regulation", body: "Community, ritual, and shared cadence as reliable regulators of individual physiology." },
    ]}
    representative={[
      "Behavior design & environmental cueing (Fogg)",
      "Habits, motivation, and identity (Duhigg, Clear)",
      "Choice architecture (Thaler & Sunstein)",
      "Social regulation of physiology (Sbarra, Uchino)",
    ]}
    whereItFits={{
      principles: [
        "Repeated inputs create adaptations.",
        "The environment shapes the state.",
      ],
      loopStage: "Emotional Reactivity → Matched Adaptive Intervention · behaviour is redirected while the state is active.",
      frameworkStep: "03 · Reinforce the Signal.",
      applications: [
        { label: "Habit & Behavior Change", to: "/applications/habit-change" },
        { label: "Recovery", to: "/applications/recovery" },
        { label: "Performance", to: "/applications/performance" },
      ],
    }}
    nextPillar={{ label: "Recovery Science", to: "/foundations/recovery-science" }}
  />
);

export const RecoveryScience = () => (
  <FoundationTemplate
    pageKey="recovery-science"
    breadcrumbLabel="Recovery Science"
    overline="Pillar 05 · Recovery Science"
    headline="Adaptation happens"
    headlineAccent="between doses of load."
    lead="Recovery science studies the mechanisms — sleep architecture, parasympathetic access, restorative rhythm — through which the body actually converts stimulus into adaptation. Without recovery, load is only load."
    whyItMatters={{
      title: "The signal only lands when the system can receive it.",
      paragraphs: [
        "Deep and REM sleep, autonomic downregulation, and rhythmic recovery are where consolidation happens. A physiology that cannot access recovery does not adapt — it accumulates.",
        "Adaptive Physiology™ measures and prescribes recovery inputs alongside training inputs — because the loop’s Feedback stage is where the system decides what to keep.",
      ],
    }}
    concepts={[
      { title: "Sleep architecture", body: "Deep and REM stages as the primary window for autonomic recovery and memory consolidation." },
      { title: "Parasympathetic access", body: "The vagal pathway back to a regulated state — trainable, not fixed." },
      { title: "Circadian rhythm", body: "The 24-hour biological rhythm that gates hormonal, cognitive, and metabolic recovery." },
      { title: "Restorative movement & rhythm", body: "Low-intensity, rhythmic inputs that promote adaptation without adding load." },
    ]}
    representative={[
      "Sleep, autonomic recovery & behavior (Walker, Buysse)",
      "Circadian physiology (Panda, Roenneberg)",
      "Parasympathetic recovery & training load (Kiviniemi, Plews)",
      "Recovery interventions in athletic populations (Kellmann, Halson)",
    ]}
    whereItFits={{
      principles: [
        "Recovery is when adaptation is consolidated.",
        "The nervous system adapts to the inputs it can actually process.",
      ],
      loopStage: "Parasympathetic Recovery → Recovery · restoration completes the activation cycle.",
      frameworkStep: "01 · Assess the State (recovery markers).",
      applications: [
        { label: "Stress & Burnout", to: "/applications/stress-burnout" },
        { label: "Performance", to: "/applications/performance" },
        { label: "Recovery", to: "/applications/recovery" },
      ],
    }}
    nextPillar={{ label: "Human Performance", to: "/foundations/human-performance" }}
  />
);

export const HumanPerformance = () => (
  <FoundationTemplate
    pageKey="human-performance"
    breadcrumbLabel="Human Performance"
    overline="Pillar 06 · Human Performance"
    headline="Performance is expressed"
    headlineAccent="through a regulated system."
    lead="Human performance research examines what allows an individual to convert training into sustained, repeatable output. Adaptive Physiology™ treats the discipline as evidence that the same principles behind recovery also expand the performance ceiling."
    whyItMatters={{
      title: "The ceiling is autonomic, not muscular.",
      paragraphs: [
        "Elite output is not the absence of stress; it is the physiological capacity to receive load, adapt, and return. Performance research keeps revealing that autonomic health — not raw work volume — is what determines whether a system continues to progress.",
        "Adaptive Physiology™ borrows the tools of high performance and uses them for a broader population: anyone who wants the physiology their life is asking them to have.",
      ],
    }}
    concepts={[
      { title: "HRV headroom", body: "The autonomic band inside which the system can absorb stress and continue to adapt." },
      { title: "Overreach vs overtraining", body: "Two functionally different states that current science can distinguish — and Adaptive Physiology™ tracks." },
      { title: "Adaptive capacity", body: "The physiological ability to convert training into progress, rather than into accumulated cost." },
      { title: "Performance under load", body: "The ability to hold attention, decision-making, and output when the system is under demand." },
    ]}
    representative={[
      "Autonomic training load (Plews, Buchheit, Laursen)",
      "Overreaching, recovery & readiness (Meeusen, Halson)",
      "Cognitive performance under stress (Balague, Marcora)",
      "Longevity through performance physiology (Attia, Peterson)",
    ]}
    whereItFits={{
      principles: [
        "The nervous system adapts.",
        "Intentional inputs create new adaptations.",
        "State influences behavior — including output.",
      ],
      loopStage: "Future Demand → Sympathetic Activation → Recovery · capacity is built for repeatable demand.",
      frameworkStep: "04 · Retrain the Default.",
      applications: [
        { label: "Performance", to: "/applications/performance" },
        { label: "Habit & Behavior Change", to: "/applications/habit-change" },
        { label: "Recovery", to: "/applications/recovery" },
      ],
    }}
    nextPillar={{ label: "Return to Scientific Foundations", to: "/foundations" }}
  />
);
